import React, { useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  SafeAreaView, Linking,
} from 'react-native';
import { colors, spacing, radius } from '../theme';
import { Divider } from '../components/UI';
import BackButton from '../components/BackButton';
import {
  mockOrders, ORDER_STATUS, DELIVERY_ESTIMATES,
  formatDeliveryDate, deliveryProgress, getDeliverySteps,
} from '../data/deliveryData';
import { getOrders } from '../services/DatabaseService';
import { useAuth } from '../context/AuthContext';

/* ══ Écran principal — liste des commandes ══════════════════ */
export default function OrdersTrackingScreen({ navigation }) {
  const { user } = useAuth();
  const [orders,    setOrders]   = useState(mockOrders);
  const [selected,  setSelected] = useState(null);
  const [dbLoading, setDbLoading]= useState(false);

  React.useEffect(() => {
    if (!user) return;
    setDbLoading(true);
    getOrders(user.id)
      .then(data => { if (data?.length) setOrders(data); })
      .catch(() => {})
      .finally(() => setDbLoading(false));
  }, [user?.id]);

  if (selected) return (
    <OrderDetail
      order={selected}
      onBack={() => setSelected(null)}
    />
  );

  const active    = orders.filter(o => !['delivered','cancelled','returned'].includes(o.status));
  const completed = orders.filter(o =>  ['delivered','cancelled','returned'].includes(o.status));

  return (
    <SafeAreaView style={s.safe}>
      
      <View style={s.topbarPrem}>
        <BackButton onPress={() => navigation.goBack()} />
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Text style={s.headerBadgePrem}>📦 COMMANDES</Text>
          <Text style={s.headerTitlePrem}>Mes commandes</Text>
          {orders.length > 0 && (
            <Text style={s.subtitlePrem}>{orders.length} commande{orders.length > 1 ? 's' : ''}</Text>
          )}
        </View>
        <View style={{ width: 40 }} />
      </View>
      <Divider />

      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 32 }}>

        {active.length > 0 && (
          <View style={s.section}>
            <Text style={s.sectionLabel}>En cours</Text>
            {active.map(order => (
              <OrderCard key={order.id} order={order} onPress={() => setSelected(order)} />
            ))}
          </View>
        )}

        {completed.length > 0 && (
          <View style={s.section}>
            <Text style={s.sectionLabel}>Historique</Text>
            {completed.map(order => (
              <OrderCard key={order.id} order={order} onPress={() => setSelected(order)} />
            ))}
          </View>
        )}

        {orders.length === 0 && (
          <View style={s.empty}>
            <Text style={s.emptyGlyph}>📦</Text>
            <Text style={s.emptyTitle}>Aucune commande</Text>
            <Text style={s.emptyText}>Vos commandes apparaîtront ici après votre premier achat</Text>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

/* ── Carte commande ──────────────────────────────────────── */
function OrderCard({ order, onPress }) {
  const st       = ORDER_STATUS[order.status] || ORDER_STATUS.PENDING;
  const progress = deliveryProgress(order.status);
  const firstLine = order.lines[0];
  const eta      = firstLine?.estimatedDelivery ? formatDeliveryDate(firstLine.estimatedDelivery) : null;

  return (
    <TouchableOpacity style={s.orderCard} onPress={onPress} activeOpacity={0.75}>
      {/* Header */}
      <View style={s.orderHeader}>
        <View>
          <Text style={s.orderId}>Commande #{order.id.split('_')[1]}</Text>
          <Text style={s.orderDate}>{new Date(order.date).toLocaleDateString('fr-FR', { day:'numeric', month:'long', year:'numeric' })}</Text>
        </View>
        <View style={[s.statusBadge, { backgroundColor: st.bg, borderColor: st.color + '40' }]}>
          <Text style={[s.statusText, { color: st.color }]}>{st.icon} {st.label}</Text>
        </View>
      </View>

      {/* Articles */}
      <View style={s.orderItems}>
        {order.lines.map(line => (
          <View key={line.id} style={s.orderItemRow}>
            <Text style={s.orderItemEmoji}>{line.emoji}</Text>
            <Text style={s.orderItemName} numberOfLines={1}>{line.name}</Text>
            <Text style={s.orderItemPartner}>{line.partner}</Text>
          </View>
        ))}
      </View>

      {/* Barre de progression */}
      {!['delivered','cancelled'].includes(order.status) && (
        <View style={s.progressWrap}>
          <View style={s.progressTrack}>
            <View style={[s.progressFill, { width: `${progress}%`, backgroundColor: st.color }]} />
          </View>
          <Text style={s.progressPct}>{progress}%</Text>
        </View>
      )}

      {/* ETA */}
      {eta && order.status !== 'delivered' && (
        <View style={s.etaRow}>
          <Text style={s.etaLabel}>Livraison estimée ·</Text>
          <Text style={s.etaDate}>{eta}</Text>
        </View>
      )}

      {/* Footer */}
      <View style={s.orderFooter}>
        <Text style={s.orderTotal}>{order.total} €</Text>
        <Text style={s.orderArrow}>Suivre →</Text>
      </View>
    </TouchableOpacity>
  );
}

/* ══ Détail d'une commande ══════════════════════════════════ */
function OrderDetail({ order, onBack }) {
  const [selectedLine, setSelectedLine] = useState(order.lines[0]);

  const st       = ORDER_STATUS[order.status] || ORDER_STATUS.PENDING;
  const steps    = getDeliverySteps(selectedLine.status);
  const progress = deliveryProgress(selectedLine.status);
  const eta      = selectedLine.estimatedDelivery
    ? formatDeliveryDate(selectedLine.estimatedDelivery)
    : null;

  const openTracking = () => {
    if (selectedLine.trackingNumber && selectedLine.carrier) {
      const url = getCarrierUrl(selectedLine.carrier, selectedLine.trackingNumber);
      Linking.openURL(url);
    }
  };

  return (
    <SafeAreaView style={s.safe}>
      
      <View style={s.topbar}>
        <TouchableOpacity onPress={onBack} style={s.backBtn}>
          <Text style={s.backText}>‹</Text>
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={s.title}>Commande #{order.id.split('_')[1]}</Text>
          <Text style={s.subtitle}>{new Date(order.date).toLocaleDateString('fr-FR')}</Text>
        </View>
        <View style={[s.statusBadge, { backgroundColor: st.bg, borderColor: st.color + '40' }]}>
          <Text style={[s.statusText, { color: st.color }]}>{st.icon} {st.label}</Text>
        </View>
      </View>
      <Divider />

      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 32 }}>

        {/* Sélecteur d'article si multi-lignes */}
        {order.lines.length > 1 && (
          <View style={s.lineTabs}>
            {order.lines.map(line => (
              <TouchableOpacity
                key={line.id}
                style={[s.lineTab, selectedLine.id === line.id && s.lineTabActive]}
                onPress={() => setSelectedLine(line)}
              >
                <Text style={s.lineTabEmoji}>{line.emoji}</Text>
                <Text style={[s.lineTabLabel, selectedLine.id === line.id && s.lineTabLabelActive]}
                  numberOfLines={1}>{line.name}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* ── Estimation de livraison ── */}
        <View style={s.etaCard}>
          <View style={s.etaCardTop}>
            <View>
              <Text style={s.etaCardLabel}>
                {selectedLine.status === 'delivered' ? 'Livré le' : 'Livraison estimée'}
              </Text>
              <Text style={s.etaCardDate}>
                {selectedLine.status === 'delivered'
                  ? formatDeliveryDate(selectedLine.deliveredAt)
                  : (eta || 'En cours de calcul')}
              </Text>
            </View>
            {selectedLine.carrier && (
              <View style={s.carrierBadge}>
                <Text style={s.carrierText}>{selectedLine.carrier}</Text>
              </View>
            )}
          </View>

          {/* Numéro de tracking */}
          {selectedLine.trackingNumber && (
            <TouchableOpacity style={s.trackingRow} onPress={openTracking}>
              <View>
                <Text style={s.trackingLabel}>Numéro de suivi</Text>
                <Text style={s.trackingNum}>{selectedLine.trackingNumber}</Text>
              </View>
              <Text style={s.trackingLink}>Suivre sur {selectedLine.carrier} →</Text>
            </TouchableOpacity>
          )}

          {/* Barre de progression */}
          <View style={s.progressBlock}>
            <View style={s.progressTrack}>
              <View style={[s.progressFill, {
                width: `${progress}%`,
                backgroundColor: selectedLine.status === 'delivered' ? '#1D9E75' : colors.accent.main
              }]} />
            </View>
            <Text style={s.progressLabel}>{progress}%</Text>
          </View>
        </View>

        {/* ── Étapes de livraison ── */}
        <View style={s.section}>
          <Text style={s.sectionLabel}>Étapes</Text>
          <View style={s.stepsCard}>
            {steps.map((step, idx) => (
              <View key={step.id} style={s.stepRow}>
                <View style={s.stepLeft}>
                  <View style={[
                    s.stepDot,
                    step.done && s.stepDotDone,
                    step.active && s.stepDotActive,
                  ]}>
                    {step.done && <Text style={s.stepDotCheck}>✓</Text>}
                  </View>
                  {idx < steps.length - 1 && (
                    <View style={[s.stepLine, step.done && s.stepLineDone]} />
                  )}
                </View>
                <View style={s.stepContent}>
                  <Text style={[s.stepLabel, step.done && s.stepLabelDone, step.active && s.stepLabelActive]}>
                    {step.label}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* ── Historique des événements ── */}
        {selectedLine.events?.length > 0 && (
          <View style={s.section}>
            <Text style={s.sectionLabel}>Historique détaillé</Text>
            <View style={s.eventsCard}>
              {[...selectedLine.events].reverse().map((event, idx) => (
                <View key={idx} style={s.eventRow}>
                  <View style={s.eventLeft}>
                    <View style={[s.eventDot, idx === 0 && s.eventDotActive]} />
                    {idx < selectedLine.events.length - 1 && <View style={s.eventLine} />}
                  </View>
                  <View style={s.eventContent}>
                    <Text style={[s.eventLabel, idx === 0 && { color: colors.accent.main }]}>
                      {event.label}
                    </Text>
                    <Text style={s.eventLocation}>{event.location}</Text>
                    <Text style={s.eventDate}>
                      {new Date(event.date).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
                      })}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* ── Adresse de livraison ── */}
        <View style={s.section}>
          <Text style={s.sectionLabel}>Adresse de livraison</Text>
          <View style={s.addressCard}>
            <Text style={s.addressText}>{order.deliveryAddress}</Text>
          </View>
        </View>

        {/* ── Articles de la commande ── */}
        <View style={s.section}>
          <Text style={s.sectionLabel}>Articles commandés</Text>
          {order.lines.map(line => {
            const lineSt = ORDER_STATUS[line.status] || ORDER_STATUS.CONFIRMED;
            return (
              <View key={line.id} style={s.lineCard}>
                <View style={s.lineThumb}>
                  <Text style={s.lineEmoji}>{line.emoji}</Text>
                </View>
                <View style={s.lineInfo}>
                  <Text style={s.lineName}>{line.name}</Text>
                  <Text style={s.lineMeta}>{line.partner} · {line.size}</Text>
                  <View style={[s.lineStatus, { backgroundColor: lineSt.bg }]}>
                    <Text style={[s.lineStatusText, { color: lineSt.color }]}>
                      {lineSt.icon} {lineSt.label}
                    </Text>
                  </View>
                </View>
                <Text style={s.linePrice}>{line.price} €</Text>
              </View>
            );
          })}
        </View>

        {/* ── Total ── */}
        <View style={s.totalCard}>
          <View style={s.totalRow}>
            <Text style={s.totalLabel}>Sous-total</Text>
            <Text style={s.totalVal}>{order.total} €</Text>
          </View>
          <View style={s.totalRow}>
            <Text style={s.totalLabel}>Livraison</Text>
            <Text style={[s.totalVal, { color: '#1D9E75' }]}>Offerte</Text>
          </View>
          <View style={[s.totalRow, s.totalFinalRow]}>
            <Text style={s.totalFinalLabel}>Total payé</Text>
            <Text style={s.totalFinalVal}>{order.total} €</Text>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

/* ── Carrier URL helper ──────────────────────────────────── */
function getCarrierUrl(carrier, trackingNumber) {
  const urls = {
    'DHL Express': `https://www.dhl.com/fr-fr/home/tracking.html?tracking-id=${trackingNumber}`,
    'DHL':         `https://www.dhl.com/fr-fr/home/tracking.html?tracking-id=${trackingNumber}`,
    'DPD':         `https://www.dpd.fr/trace/${trackingNumber}`,
    'UPS':         `https://www.ups.com/track?tracknum=${trackingNumber}`,
    'FedEx':       `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`,
    'Colissimo':   `https://www.laposte.fr/outils/suivre-vos-envois?code=${trackingNumber}`,
    'Chronopost':  `https://www.chronopost.fr/tracking-no-cms/suivi-page?listeNumerosLT=${trackingNumber}`,
  };
  return urls[carrier] || `https://www.google.com/search?q=${carrier}+tracking+${trackingNumber}`;
}

/* ── Styles ──────────────────────────────────────────────── */
const s = StyleSheet.create({
  safe:           { flex: 1, backgroundColor: '#FFFFFF' },
  topbar:         { flexDirection: 'row', alignItems: 'center', paddingHorizontal: spacing.xl, paddingBottom: spacing.md },
  topbarCenter:   { flex: 1, alignItems: 'center' },
  backBtn:        { width: 36, height: 36, alignItems: 'center', justifyContent: 'center' },
  backIcon:       { fontSize: 28, color: colors.text.primary, lineHeight: 32, marginTop: -2 },
  backBtn:        { width: 28 },
  backText:       { fontSize: 22, color: colors.text.secondary, lineHeight: 26 },
  title:          { fontSize: 13, letterSpacing: 4, color: colors.text.secondary, textTransform: 'uppercase' },
  subtitle:       { fontSize: 8, letterSpacing: 2, color: colors.text.faint, marginTop: 2 },

  section:        { paddingHorizontal: spacing.xl, marginBottom: spacing.md },
  sectionLabel:   { fontSize: 8, letterSpacing: 3, color: colors.text.faint, textTransform: 'uppercase', marginBottom: spacing.sm },

  orderCard:      { marginHorizontal: spacing.xl, backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md, marginBottom: 8 },
  orderHeader:    { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.sm },
  orderId:        { fontSize: 12, color: colors.text.secondary, letterSpacing: 0.3, fontWeight: '500' },
  orderDate:      { fontSize: 9, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase', marginTop: 2 },
  statusBadge:    { paddingHorizontal: 8, paddingVertical: 3, borderRadius: radius.pill, borderWidth: 0.5 },
  statusText:     { fontSize: 9, letterSpacing: 1, fontWeight: '500' },

  orderItems:     { marginBottom: spacing.sm },
  orderItemRow:   { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 3 },
  orderItemEmoji: { fontSize: 14 },
  orderItemName:  { flex: 1, fontSize: 11, color: colors.text.secondary },
  orderItemPartner:{ fontSize: 9, letterSpacing: 1, color: colors.text.faint },

  progressWrap:   { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  progressTrack:  { flex: 1, height: 3, backgroundColor: colors.bg.border, borderRadius: 2, overflow: 'hidden' },
  progressFill:   { height: '100%', borderRadius: 2 },
  progressPct:    { fontSize: 9, color: colors.text.faint, minWidth: 28, textAlign: 'right' },

  etaRow:         { flexDirection: 'row', gap: 4, alignItems: 'center' },
  etaLabel:       { fontSize: 9, letterSpacing: 1, color: colors.text.faint },
  etaDate:        { fontSize: 9, letterSpacing: 1, color: colors.accent.main, fontWeight: '500' },

  orderFooter:    { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingTop: spacing.sm, marginTop: spacing.sm, borderTopWidth: 0.5, borderTopColor: colors.bg.border },
  orderTotal:     { fontSize: 14, color: colors.text.primary, fontWeight: '200', letterSpacing: 0.5 },
  orderArrow:     { fontSize: 9, letterSpacing: 2, color: colors.accent.main, textTransform: 'uppercase' },

  lineTabs:       { flexDirection: 'row', gap: 6, paddingHorizontal: spacing.xl, paddingTop: spacing.md, paddingBottom: spacing.sm },
  lineTab:        { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: '#FFFFFF', borderRadius: radius.sm, borderWidth: 0.5, borderColor: colors.bg.border, padding: 8 },
  lineTabActive:  { borderColor: colors.accent.main, backgroundColor: colors.accent.ghost },
  lineTabEmoji:   { fontSize: 14 },
  lineTabLabel:   { flex: 1, fontSize: 9, color: colors.text.faint, letterSpacing: 0.5 },
  lineTabLabelActive: { color: colors.accent.main },

  etaCard:        { marginHorizontal: spacing.xl, backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md, marginBottom: spacing.md },
  etaCardTop:     { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.sm },
  etaCardLabel:   { fontSize: 8, letterSpacing: 2.5, color: colors.text.faint, textTransform: 'uppercase', marginBottom: 4 },
  etaCardDate:    { fontSize: 15, color: colors.text.primary, fontWeight: '200', letterSpacing: 0.5 },
  carrierBadge:   { backgroundColor: colors.bg.surface, borderRadius: radius.pill, borderWidth: 0.5, borderColor: colors.bg.border, paddingHorizontal: 9, paddingVertical: 4 },
  carrierText:    { fontSize: 9, letterSpacing: 2, color: colors.text.faint, textTransform: 'uppercase' },
  trackingRow:    { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: spacing.sm, borderTopWidth: 0.5, borderTopColor: colors.bg.border, marginBottom: spacing.sm },
  trackingLabel:  { fontSize: 8, letterSpacing: 2, color: colors.text.faint, textTransform: 'uppercase', marginBottom: 3 },
  trackingNum:    { fontSize: 11, color: colors.text.secondary, fontFamily: 'monospace', letterSpacing: 1 },
  trackingLink:   { fontSize: 9, letterSpacing: 1.5, color: colors.accent.main, textTransform: 'uppercase' },
  progressBlock:  { flexDirection: 'row', alignItems: 'center', gap: 8 },
  progressLabel:  { fontSize: 9, color: colors.text.faint, minWidth: 30, textAlign: 'right' },

  stepsCard:      { backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md },
  stepRow:        { flexDirection: 'row', gap: 10, minHeight: 36 },
  stepLeft:       { alignItems: 'center', width: 20 },
  stepDot:        { width: 18, height: 18, borderRadius: 9, borderWidth: 0.5, borderColor: colors.bg.border, backgroundColor: colors.bg.surface, alignItems: 'center', justifyContent: 'center' },
  stepDotDone:    { backgroundColor: colors.accent.ghost, borderColor: colors.accent.main },
  stepDotActive:  { backgroundColor: colors.accent.main, borderColor: colors.text.primary },
  stepDotCheck:   { fontSize: 9, color: colors.accent.main },
  stepLine:       { flex: 1, width: 1, backgroundColor: colors.bg.border, marginVertical: 2 },
  stepLineDone:   { backgroundColor: colors.text.faint },
  stepContent:    { flex: 1, paddingBottom: 10 },
  stepLabel:      { fontSize: 11, color: colors.text.faint, letterSpacing: 0.3, paddingTop: 2 },
  stepLabelDone:  { color: colors.text.secondary },
  stepLabelActive:{ color: colors.accent.main, fontWeight: '500' },

  eventsCard:     { backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md },
  eventRow:       { flexDirection: 'row', gap: 10, minHeight: 52 },
  eventLeft:      { alignItems: 'center', width: 12 },
  eventDot:       { width: 8, height: 8, borderRadius: 4, backgroundColor: colors.bg.border, marginTop: 4 },
  eventDotActive: { backgroundColor: colors.accent.main },
  eventLine:      { flex: 1, width: 1, backgroundColor: colors.bg.border, marginVertical: 2 },
  eventContent:   { flex: 1, paddingBottom: 12 },
  eventLabel:     { fontSize: 12, color: colors.text.secondary, letterSpacing: 0.3, marginBottom: 2 },
  eventLocation:  { fontSize: 9, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase' },
  eventDate:      { fontSize: 9, color: colors.text.faint, marginTop: 2 },

  addressCard:    { backgroundColor: '#FFFFFF', borderRadius: radius.sm, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md },
  addressText:    { fontSize: 12, color: colors.text.secondary, letterSpacing: 0.3, lineHeight: 18 },

  lineCard:       { flexDirection: 'row', gap: 10, alignItems: 'center', backgroundColor: '#FFFFFF', borderRadius: radius.sm, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md, marginBottom: 6 },
  lineThumb:      { width: 40, height: 48, backgroundColor: colors.bg.surface, borderRadius: radius.sm, alignItems: 'center', justifyContent: 'center', borderWidth: 0.5, borderColor: colors.bg.border },
  lineEmoji:      { fontSize: 18 },
  lineInfo:       { flex: 1 },
  lineName:       { fontSize: 12, color: colors.text.secondary, letterSpacing: 0.3 },
  lineMeta:       { fontSize: 9, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase', marginTop: 2 },
  lineStatus:     { alignSelf: 'flex-start', borderRadius: radius.pill, paddingHorizontal: 7, paddingVertical: 2, marginTop: 4 },
  lineStatusText: { fontSize: 8, letterSpacing: 1, fontWeight: '500' },
  linePrice:      { fontSize: 13, color: colors.text.primary, fontWeight: '200' },

  totalCard:      { marginHorizontal: spacing.xl, backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md },
  totalRow:       { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5 },
  totalLabel:     { fontSize: 10, color: colors.text.faint, letterSpacing: 1.5, textTransform: 'uppercase' },
  totalVal:       { fontSize: 12, color: colors.text.secondary },
  totalFinalRow:  { borderTopWidth: 0.5, borderTopColor: colors.bg.border, paddingTop: 8, marginTop: 4 },
  totalFinalLabel:{ fontSize: 11, color: colors.accent.main, letterSpacing: 2, textTransform: 'uppercase', fontWeight: '500' },
  totalFinalVal:  { fontSize: 16, color: colors.text.primary, fontWeight: '200', letterSpacing: 0.5 },

  empty:          { paddingTop: 60, alignItems: 'center', paddingHorizontal: spacing.xl },
  emptyGlyph:     { fontSize: 32, marginBottom: spacing.md },
  emptyTitle:     { fontSize: 12, letterSpacing: 3, color: colors.text.secondary, textTransform: 'uppercase', marginBottom: 6 },
  emptyText:      { fontSize: 11, color: colors.text.faint, textAlign: 'center', lineHeight: 18 },

  // ═══ Style premium ═══
  topbarPrem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 14, backgroundColor: '#FAFAFA',
    borderBottomWidth: 0.5, borderBottomColor: '#E8E0C8',
  },
  backBtnPrem:   { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  backTxtPrem:   { fontSize: 32, color: '#1A1A1A', fontWeight: '300' },
  headerBadgePrem: { fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: 4 },
  headerTitlePrem: { fontSize: 18, fontWeight: '300', color: '#1A1A1A', letterSpacing: 0.3 },
  subtitlePrem:  { fontSize: 11, color: '#999', marginTop: 2, letterSpacing: 0.5 },
});
